import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, LogOut, RefreshCw, ShieldCheck, UserPlus } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import ChangePasswordModal from "@/components/admin/ChangePasswordModal";
import RequireAdmin from "@/components/admin/RequireAdmin";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useAdminI18n } from "@/lib/adminI18n";

const ROLES = ["owner", "admin", "manager", "staff", "viewer"];
const STATUSES = ["active", "invited", "suspended", "disabled"];

function permissionLabels(item) {
  const labels = Array.isArray(item?.permission_labels) ? item.permission_labels : [];
  if (labels.length) return labels;
  return Array.isArray(item?.permissions) ? item.permissions : [];
}

function AdminSettingsPageInner() {
  const { apiFetch, user, hasPermission, logout } = useAdminAuth();
  const { t, lang, setLang, isRTL } = useAdminI18n();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [presets, setPresets] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "staff", temporary_password: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [summaryRes, staffRes, rolesRes] = await Promise.all([
        apiFetch("/admin/settings/security-summary"),
        apiFetch("/admin/staff"),
        apiFetch("/admin/roles"),
      ]);
      const [summaryData, staffData, rolesData] = await Promise.all([
        summaryRes.json().catch(() => ({})),
        staffRes.json().catch(() => ({})),
        rolesRes.json().catch(() => ({})),
      ]);
      if (!summaryRes.ok) throw new Error(summaryData?.detail || "Failed to load settings");
      setSummary(summaryData);
      setStaff(Array.isArray(staffData.items) ? staffData.items : []);
      setRoles(Array.isArray(rolesData.roles) ? rolesData.roles : []);
      setPresets(Array.isArray(rolesData.presets) ? rolesData.presets : []);
    } catch (e) {
      toast.error(e?.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createStaff = async (e) => {
    e.preventDefault();
    if (!hasPermission("staff.manage")) {
      toast.error("This role cannot create staff accounts");
      return;
    }
    try {
      const res = await apiFetch("/admin/staff", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          temporary_password: form.temporary_password || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Could not create staff account");
      setForm({ email: "", name: "", role: "staff", temporary_password: "" });
      toast.success(data.temporary_password ? `Created. Temporary password: ${data.temporary_password}` : "Staff account created");
      load();
    } catch (err) {
      toast.error(err?.message || "Create failed");
    }
  };

  const updateStaff = async (staffId, patch) => {
    try {
      const res = await apiFetch(`/admin/staff/${encodeURIComponent(staffId)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Update failed");
      setStaff((items) => items.map((item) => (item.id === staffId ? data.item : item)));
      toast.success("Staff account updated");
    } catch (err) {
      toast.error(err?.message || "Update failed");
    }
  };

  const resetStaffPassword = async (staffId) => {
    const temporaryPassword = window.prompt("Enter a temporary password with at least 8 characters");
    if (!temporaryPassword) return;
    try {
      const res = await apiFetch(`/admin/staff/${encodeURIComponent(staffId)}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ temporary_password: temporaryPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Password reset failed");
      setStaff((items) => items.map((item) => (item.id === staffId ? data.item : item)));
      toast.success("Password reset. Share the temporary password securely.");
    } catch (err) {
      toast.error(err?.message || "Password reset failed");
    }
  };

  return (
    <AdminShell
      title={t("settings.title", "Settings")}
      description={t("settings.description", "Manage admin roles, source separation, login security, and account controls for the website admin.")}
      actions={(
        <button type="button" onClick={load} className="admin-shell-action">
          <RefreshCw className="h-4 w-4" />
          <span>{t("dashboard.refresh", "Refresh")}</span>
        </button>
      )}
    >
      {loading ? (
        <div className="rounded-3xl border border-white/10 bg-white/[0.045] p-8 text-sm text-white/55">Loading settings...</div>
      ) : (
        <div className="space-y-6">
          <section className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
            <div className="mb-4 flex items-center gap-2 text-white">
              <ShieldCheck className="h-5 w-5 text-[#F58D4A]" />
              <h2 className="text-lg font-semibold">{t("settings.accountControls", "Account controls")}</h2>
            </div>
            <div className={`grid gap-3 md:grid-cols-3 ${isRTL ? "text-right" : ""}`}>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">{t("shell.language", "Language")}</div>
                <div className="mt-3 inline-flex rounded-full border border-white/10 bg-white/[0.04] p-1">
                  {[
                    { id: "en", label: t("shell.english", "English") },
                    { id: "ar", label: t("shell.arabic", "Arabic") },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setLang(item.id)}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold ${lang === item.id ? "bg-[#F37021] text-white" : "text-white/70"}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              <button type="button" onClick={() => setPwdOpen(true)} className="rounded-2xl border border-white/10 bg-black/20 p-4 text-left text-white transition hover:border-[#F37021]/35">
                <KeyRound className="mb-3 h-5 w-5 text-[#F58D4A]" />
                <div className="font-semibold">{t("shell.password", "Password")}</div>
                <div className="mt-1 text-xs text-white/45">{t("settings.changePasswordHint", "Change your own admin password.")}</div>
              </button>
              <button type="button" onClick={logout} className="rounded-2xl border border-white/10 bg-black/20 p-4 text-left text-white transition hover:border-red-400/35">
                <LogOut className="mb-3 h-5 w-5 text-red-300" />
                <div className="font-semibold">{t("shell.signOut", "Sign out")}</div>
                <div className="mt-1 text-xs text-white/45">{user?.email}</div>
              </button>
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-4">
            {[
              [t("settings.role", "Role"), user?.role || "admin"],
              [t("settings.pendingApprovals", "Pending approvals"), summary?.counts?.pending_approvals ?? 0],
              [t("settings.unreadNotifications", "Unread notifications"), summary?.counts?.unread_notifications ?? 0],
              [t("settings.lockedLogins", "Locked logins"), summary?.counts?.locked_logins ?? 0],
            ].map(([label, value]) => (
              <div key={label} className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/35">{label}</div>
                <div className="mt-2 text-2xl font-semibold text-white">{value}</div>
              </div>
            ))}
          </section>

          <section className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
            <div className="mb-4 flex items-center gap-2 text-white">
              <ShieldCheck className="h-5 w-5 text-[#F58D4A]" />
              <h2 className="text-lg font-semibold">{t("settings.sourcePolicy", "Source and sync policy")}</h2>
            </div>
            <div className="grid gap-3 text-sm text-white/65 md:grid-cols-2">
              <div>{t("settings.websiteCustomers", "Website customers")}: {summary?.sources?.website_customers}</div>
              <div>{t("settings.mobileApp", "Mobile app")}: {summary?.sources?.mobile_app}</div>
              <div>{t("settings.contentWorkflow", "Content workflow")}: {summary?.sources?.content}</div>
              <div>{t("settings.customerIds", "Customer IDs")}: {summary?.sync?.customer_id_policy}</div>
            </div>
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.1fr_1.6fr]">
            <form onSubmit={createStaff} className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
              <div className="mb-4 flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-[#F58D4A]" />
                <h2 className="text-lg font-semibold">{t("settings.employeeAccounts", "Employee/admin accounts")}</h2>
              </div>
              <p className="mb-3 text-xs text-white/45">{t("settings.employeeDomainHint", "Employee emails must end with @bobserviceslb.com.")}</p>
              <div className="space-y-3">
                <TextField label={t("settings.name", "Name")} value={form.name} onChange={(name) => setForm((f) => ({ ...f, name }))} />
                <TextField label={t("settings.email", "Email")} type="email" value={form.email} onChange={(email) => setForm((f) => ({ ...f, email }))} />
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/55">{t("settings.role", "Role")}</span>
                  <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="mt-1.5 h-10 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-white">
                    {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
                  </select>
                </label>
                <TextField label={t("settings.temporaryPassword", "Temporary password")} type="password" value={form.temporary_password} onChange={(temporary_password) => setForm((f) => ({ ...f, temporary_password }))} />
                <button disabled={!form.email || !form.name} className="h-11 w-full rounded-full bg-[#F37021] font-semibold text-white disabled:opacity-50">
                  {t("settings.createAccount", "Create account")}
                </button>
              </div>
            </form>

            <div className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
              <h2 className="mb-4 text-lg font-semibold">{t("settings.currentStaff", "Current staff")}</h2>
              <div className="space-y-3">
                {staff.map((item) => (
                  <div key={item.id} className="grid gap-3 rounded-2xl border border-white/10 bg-black/20 p-4 md:grid-cols-[1fr_auto_auto_auto] md:items-center">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-white">{item.name || item.email}</div>
                      <div className="truncate text-xs text-white/45">{item.email}</div>
                    </div>
                    <select value={item.role} onChange={(e) => updateStaff(item.id, { role: e.target.value })} className="h-10 rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-white">
                      {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
                    </select>
                    <select value={item.status || "active"} onChange={(e) => updateStaff(item.id, { status: e.target.value })} className="h-10 rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-white">
                      {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                    <button type="button" onClick={() => resetStaffPassword(item.id)} className="h-10 rounded-xl border border-white/10 px-3 text-xs font-semibold text-white/75 hover:border-[#F37021]/35">
                      {t("settings.resetPassword", "Reset password")}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
            <h2 className="mb-4 text-lg font-semibold">{t("settings.rolePermissions", "Role permissions")}</h2>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-black/35 text-[11px] uppercase tracking-[0.18em] text-white/45">
                  <tr>
                    <th className="px-4 py-3">{t("settings.role", "Role")}</th>
                    <th className="px-4 py-3">{t("settings.permissions", "Permissions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {roles.map((role) => (
                    <tr key={role.role}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white">{role.label}</div>
                        {role.description ? <div className="mt-1 text-xs text-white/45">{role.description}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-white/60">{permissionLabels(role).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {presets.length > 0 && (
              <div className="mt-5">
                <h3 className="mb-3 text-sm font-semibold text-white/80">{t("settings.presets", "Presets")}</h3>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                  {presets.map((preset) => (
                    <div key={preset.id} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                      <div className="font-semibold text-white">{preset.label}</div>
                      <div className="mt-1 text-xs text-[#F58D4A]">{preset.role}</div>
                      <div className="mt-2 text-xs leading-5 text-white/50">{permissionLabels(preset).join(", ")}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      )}
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </AdminShell>
  );
}

function TextField({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/55">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-10 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-white focus:border-[#F37021] focus:outline-none" />
    </label>
  );
}

export default function AdminSettingsPage() {
  return (
    <RequireAdmin permission="settings.manage">
      <AdminSettingsPageInner />
    </RequireAdmin>
  );
}
